import React, { useRef, useEffect, useState } from 'react';
import jsPDF from 'jspdf';
import { FaPrint } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import axios from "axios"; 

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL; // Use your actual base URL

const InvoiceView = () => {
  const location = useLocation();
  const printRef = useRef();
  const [invoice, setInvoice] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState(null);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const invoiceData = queryParams.get('data');
  
    if (invoiceData) {
      const parsedInvoice = JSON.parse(decodeURIComponent(invoiceData));
      setInvoice(parsedInvoice);

    }
  }, []);
  
  useEffect(() => {
    if (invoice) {
      setInvoiceNumber(invoice.invoiceNumber);
      getInvoiceById(invoice.invoiceNumber); // Move this inside the check
    }
  }, [invoice]); // Change dependency to 'invoice' to ensure it runs when 'invoice' changes
  
  const getInvoiceById = async (invoiceNumber) => {
    try{
        const response = await axios.get(`${API_BASE_URL}/invoices/get/${invoiceNumber}`);

        const invoicedata = {
            //id: response.data._id,
            invoiceNumber: response.data.invoice_id,   
            salesOrderNumber: response.data.sales_order_id.sales_order_id,
            pickupLocation: response.data.pickup_location_id.location_name,
            latitude: response.data.pickup_location_id.latitude,
            longitude: response.data.pickup_location_id.longitude,
            customerId: response.data.customer_id.customer_code,
            customerName: response.data.customer_id.customer_name || "Unknown",
            customerMobile: response.data.customer_id.cus_mobileno || "Unknown",
            taxAmount: response.data.sales_order_id.tax_amount || 0,
            finalAmount: response.data.sales_order_id.final_amount || 0,
            dueDate: response.data.due_date 
            ? new Date(response.data.due_date).toLocaleDateString("en-GB")
            : "N/A",
            items: response.data.sales_order_id.items.map((item) => ({
                productCode: item.product_id.product_code || "N/A",
                productName: item.product_id.product_name || "N/A",
                quantity: item.approved_qty || 0,
                unit: item.product_id.unit?.unit_name || "N/A",
                unitPrice: item.unit_price || 0,
                totalPrice: item.total_price|| 0,
            })),
        };


        setInvoiceData(invoicedata);

    }catch (error){
        console.error("Error fetching invoice:", error);
        //toast.error("Failed to fetch invoice. Please try again.");
        return null;
    }
  };

  const handlePrint = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const leMargin = 10;
    const margin = 16;
  
    let y = 10;

    doc.setDrawColor(0); // Black border
    doc.rect(leMargin, leMargin, pageWidth - 2 * leMargin, pageHeight - 2 * leMargin);
  
    // Logo (optional: requires logo base64 or public URL with CORS enabled)
    const logo = new Image();
    logo.src = "/logo.png"; // path in public folder
    doc.addImage(logo, "PNG", margin, y, 30, 30);
  
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("Invoice", pageWidth - 30, y + 12, { align: "right" });
  
    y += 35;
  
    // Invoice and customer info
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);

    // Invoice Number
    doc.setFont("helvetica", "bold");
    doc.text("Invoice Number:", margin, y);
    let labelWidth = doc.getTextWidth("Invoice Number: ");
    doc.setFont("helvetica", "normal");
    doc.text(invoiceData.invoiceNumber, margin + labelWidth + 1, y);
    y += 6;

    // Sales Order Number
    doc.setFont("helvetica", "bold");
    doc.text("Sales Order Number:", margin, y);
    labelWidth = doc.getTextWidth("Sales Order Number: ");
    doc.setFont("helvetica", "normal");
    doc.text(invoiceData.salesOrderNumber, margin + labelWidth + 1, y);
    y += 6;

    // Customer ID
    doc.setFont("helvetica", "bold");
    doc.text("Customer ID:", margin, y);
    labelWidth = doc.getTextWidth("Customer ID: ");
    doc.setFont("helvetica", "normal");
    doc.text(invoiceData.customerId, margin + labelWidth + 1, y);
    y += 6;

    // Pickup Location (with link)
    doc.setFont("helvetica", "bold");
    doc.text("Pickup Location:", margin, y);
    labelWidth = doc.getTextWidth("Pickup Location: ");
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 255);
    doc.textWithLink(invoiceData.pickupLocation, margin + labelWidth + 1, y, {
      url: `https://www.google.com/maps?q=${invoiceData.latitude},${invoiceData.longitude}`,
    });
    doc.setTextColor(0, 0, 0);
    y += 6;

    // Tax Amount
    doc.setFont("helvetica", "bold");
    doc.text("Tax Amount:", margin, y);
    labelWidth = doc.getTextWidth("Tax Amount: ");
    doc.setFont("helvetica", "normal");
    doc.text(`LKR ${invoiceData.taxAmount.toFixed(2)}`, margin + labelWidth + 1, y);
    y += 6;

    // Final Amount
    doc.setFont("helvetica", "bold");
    doc.text("Final Amount:", margin, y);
    labelWidth = doc.getTextWidth("Final Amount: ");
    doc.setFont("helvetica", "normal");
    doc.text(`LKR ${invoiceData.finalAmount.toFixed(2)}`, margin + labelWidth + 1, y);
    y += 6;

    // Due Date
    doc.setFont("helvetica", "bold");
    doc.text("Due Date:", margin, y);
    labelWidth = doc.getTextWidth("Due Date: ");
    doc.setFont("helvetica", "normal");
    doc.text(invoiceData.dueDate, margin + labelWidth + 1, y);
    y += 10;
  
    // Table headers + data
    doc.autoTable({
      startY: y,
      head: [[
        "Product Code",
        "Product Name",
        "Quantity",
        "Unit",
        "Unit Price (LKR)",
        "Total Price (LKR)",
      ]],
      body: invoiceData.items.map((item) => [
        item.productCode,
        item.productName,
        item.quantity,
        item.unit,
        item.unitPrice.toFixed(2),
        item.totalPrice.toFixed(2),
      ]),
      styles: {
        fontSize: 10,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [220, 220, 220],
        textColor: 0,
        fontStyle: "bold",
        // ⚠️ Don't include halign here, or it overrides column-specific settings
      },
      columnStyles: {
        0: { halign: "center" }, // Product Code
        1: { halign: "left" },   // Product Name
        2: { halign: "center" }, // Quantity
        3: { halign: "center" }, // Unit
        4: { halign: "right" },  // Unit Price
        5: { halign: "right" },  // Total Price
      },
      useCss: false, // ✅ Ensures custom alignment works properly
    });
    
  
    // Footer
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text("Thank you for your business!", pageWidth / 2, finalY, { align: "center" });
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, pageWidth / 2, finalY + 5, { align: "center" });
  
    // Save
    const fileName = `Invoice_${invoiceData.invoiceNumber}.pdf`;
    doc.save(fileName);
  };
  
  // Ensure invoice data is loaded before rendering
  if (!invoiceData) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center p-4 sm:p-6">
      <div ref={printRef} className="w-full max-w-3xl bg-white shadow-md rounded-lg p-4 sm:p-6">
        <div className="border-2 border-black p-4 rounded-md">
          {/* Logo and Header */}
          <div className="flex flex-wrap justify-between items-center mb-4">
            <img src="/agro_farm_ims_logo.png" alt="Logo" className="w-20 h-20 sm:w-28 sm:h-28" />
            <h2 className="text-lg sm:text-3xl font-bold">Invoice</h2>
          </div>

          {/* Invoice Details */}
          <div className="mb-6 text-sm sm:text-base">
            <p><strong>Invoice Number:</strong> {invoiceData.invoiceNumber}</p>
            <p><strong>Sales Order Number:</strong> {invoiceData.salesOrderNumber}</p>
            <p><strong>Customer ID:</strong> {invoiceData.customerId}</p>
            <p>
              <strong>Pickup Location Name:</strong>{" "}
              <a
                href={`https://www.google.com/maps?q=${invoiceData.latitude},${invoiceData.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline hover:text-blue-800"
              >
                {invoiceData.pickupLocation}
              </a>
            </p>
            <p><strong>Tax Amount:</strong> LKR {invoiceData.taxAmount.toFixed(2)}</p>
            <p><strong>Final Amount:</strong> LKR {invoiceData.finalAmount.toFixed(2)}</p>
            <p><strong>Due Date: </strong> {invoiceData.dueDate}</p>
          </div>

          <div className="mb-6">
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3">Product Code</th>
                  <th scope="col" className="px-6 py-3">Product Name</th>
                  <th scope="col" className="px-6 py-3">Quantity</th>
                  <th scope="col" className="px-6 py-3">Unit</th>
                  <th scope="col" className="px-6 py-3">Unit Price</th>
                  <th scope="col" className="px-6 py-3">Total Price</th>
                </tr>
              </thead>
              <tbody>
              {invoiceData.items.map((item, index) => (
                <tr key={index} className="bg-white border-b">
                  <td className="px-6 py-4">{item.productCode}</td>
                  <td className="px-6 py-4">{item.productName}</td>
                  <td className="px-6 py-4">{item.quantity}</td>
                  <td className="px-6 py-4">{item.unit}</td>
                  <td className="px-6 py-4">
                    {new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(item.unitPrice)}
                  </td>
                  <td className="px-6 py-4">
                    {new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(item.totalPrice)}
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
        </div>
          {/* Footer */}
          <div className="text-center mt-4">
            <p className="text-xs sm:text-sm text-gray-600">Thank you for your business!</p>
            <p className="text-xs sm:text-sm text-gray-600">Generated on {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Print Button */}
      <div className="mt-6 flex justify-center">
        <button
          onClick={handlePrint}
          className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 flex items-center"
        >
          <FaPrint className="mr-2" /> Print
        </button>
      </div>
    </div>
  );
};

export default InvoiceView;